import { useState, useEffect } from 'react';
import { Send, MessageSquare } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import Button from '../ui/Button';
import Textarea from '../ui/Textarea';

interface Comment {
  id: string;
  user_id: string;
  comment_text: string;
  time_logged: string | null;
  created_at: string;
  updated_at: string;
}

interface CommentsSectionProps {
  taskId?: string;
  projectId?: string;
  className?: string;
}

export function CommentsSection({ taskId, projectId, className = '' }: CommentsSectionProps) {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (taskId || projectId) {
      loadComments();
      const cleanup = subscribeToComments();
      return cleanup;
    }
  }, [taskId, projectId]);

  const loadComments = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('apro_comments')
        .select('*')
        .order('created_at', { ascending: false });

      if (taskId) {
        query = query.eq('task_id', taskId);
      } else if (projectId) {
        query = query.eq('project_id', projectId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setComments(data || []);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToComments = () => {
    const channelName = taskId ? `comments-task-${taskId}` : `comments-project-${projectId}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'apro_comments',
          filter: taskId ? `task_id=eq.${taskId}` : `project_id=eq.${projectId}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setComments(prev => [payload.new as Comment, ...prev]);
          } else if (payload.eventType === 'DELETE') {
            setComments(prev => prev.filter(c => c.id !== payload.old.id));
          } else if (payload.eventType === 'UPDATE') {
            setComments(prev => prev.map(c => c.id === payload.new.id ? payload.new as Comment : c));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !user) return;

    setSubmitting(true);
    try {
      const commentData: any = {
        user_id: user.id,
        comment_text: newComment.trim(),
      };

      if (taskId) {
        commentData.task_id = taskId;
      } else if (projectId) {
        commentData.project_id = projectId;
      }

      const { error } = await supabase
        .from('apro_comments')
        .insert([commentData]);

      if (error) throw error;

      setNewComment('');
    } catch (error) {
      console.error('Error creating comment:', error);
      alert('Error al crear el comentario');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTimeLogged = (interval: string | null) => {
    if (!interval) return null;

    const hours = parseInt(interval.split(':')[0]);
    const minutes = parseInt(interval.split(':')[1]);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  return (
    <div className={`flex flex-col h-full bg-gray-900 ${className}`}>
      <div className="p-4 border-b border-gray-800 bg-gray-900">
        <h3 className="text-sm font-medium text-gray-200 flex items-center gap-2">
          <MessageSquare className="w-4 h-4" />
          Activity
        </h3>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="text-center text-gray-500 text-sm">Cargando comentarios...</div>
        ) : comments.length === 0 ? (
          <div className="text-center text-gray-600 text-sm py-8">
            No hay comentarios aún
          </div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-brand-600 flex items-center justify-center text-white text-xs font-medium">
                {user?.email?.[0].toUpperCase() || 'U'}
              </div>
              <div className="flex-1">
                <div className="bg-gray-800 rounded-panel p-3 border border-gray-700">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium text-gray-200">
                      {user?.email?.split('@')[0] || 'Usuario'}
                    </span>
                    {comment.time_logged && (
                      <span className="text-xs bg-brand-900 text-brand-200 px-2 py-0.5 rounded">
                        {formatTimeLogged(comment.time_logged)}
                      </span>
                    )}
                    <span className="text-xs text-gray-500 ml-auto">
                      {formatDistanceToNow(new Date(comment.created_at), {
                        addSuffix: true,
                        locale: es,
                      })}
                    </span>
                  </div>
                  <p className="text-sm text-gray-300 whitespace-pre-wrap">
                    {comment.comment_text}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="p-4 border-t border-gray-800 bg-gray-900">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Escribe un comentario..."
            rows={4}
            className="flex-1"
            disabled={submitting}
          />
          <Button
            type="submit"
            variant="primary"
            iconLeft={Send}
            disabled={!newComment.trim() || submitting}
            className="self-end"
            aria-label="Enviar comentario"
          />
        </form>
      </div>
    </div>
  );
}
